import React, {createContext, useState, useCallback} from 'react'
import {Elements, RecurlyProvider, useRecurly} from '@recurly/react-recurly'
import {toast} from 'react-toastify'

/**
 * @typedef {{
 * recurlyToken:  {id: string,type: string} | null,
 * clearRecurlyToken: () => void,
 * isFetching: boolean,
 * fetchToken: (form: HTMLFormElement)=>void,
 * fetchBankAccountToken: (form: HTMLFormElement)=>void,
 * scriptLoaded: boolean,
 * }} RecurlyPaymentContextType
 */

const RecurlyPaymentContext = createContext(null)

export const RecurlyPaymentProvider = ({children}) => {
  const [recurlyToken, setRecurlyToken] = useState(null)
  const [isFetching, setIsFetching] = useState(false)
  const [scriptLoaded, setScriptLoaded] = useState(false)
  // const {isMobileViewEnab}
  const [type, setType] = useState('card')

  const clearRecurlyToken = useCallback(() => {
    setRecurlyToken(null)
  }, [])

  React.useEffect(() => {
    const recurlyStyles = document.createElement('link')
    const script = document.createElement('script')

    recurlyStyles.href = 'https://js.recurly.com/v4/recurly.css'
    recurlyStyles.rel = 'stylesheet'
    document.body.appendChild(recurlyStyles)

    script.src = 'https://js.recurly.com/v4/recurly.js'
    script.async = true
    script.id = 'recurlyScript'

    document.body.appendChild(script)
    document.getElementById('recurlyScript').addEventListener('load', () => {
      setScriptLoaded(true)
    })
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script)
      }
      if (document.body.contains(recurlyStyles)) {
        document.body.removeChild(recurlyStyles)
      }
    }
  }, [])

  if (!scriptLoaded) return null

  return (
    <RecurlyProvider publicKey={process.env.REACT_APP_RECURLY_PUBLIC_KEY}>
      <RecurlyPaymentContext.Provider
        value={{
          recurlyToken,
          setRecurlyToken,
          isFetching,
          clearRecurlyToken,
          setIsFetching,
          scriptLoaded,
          type,
          setType,
        }}
      >
        <Elements>{children}</Elements>
      </RecurlyPaymentContext.Provider>
    </RecurlyProvider>
  )
}

/**
 * @returns {RecurlyPaymentContextType}
 */
export function useRecurlyPayment() {
  const context = React.useContext(RecurlyPaymentContext)
  if (!context) {
    throw new Error(
      "'useRecurlyPayment' can't be called outside 'RecurlyPaymentProvider'",
    )
  }
  const recurly = useRecurly()

  function fetchToken(formElement) {
    context.setIsFetching(true)
    recurly.token(formElement, (err, token) => {
      context.setIsFetching(false)
      if (err) {
        toast.error(err.message)
        return undefined
      }
      context.setRecurlyToken(token)
    })
  }

  function fetchBankAccountToken(formElement) {
    context.setIsFetching(true)
    recurly.bankAccount.token(formElement, (err, token) => {
      context.setIsFetching(false)
      if (err) {
        toast.error(err.message)
        return undefined
      }
      context.setRecurlyToken(token)
    })
  }

  return {
    ...context,
    fetchToken,
    fetchBankAccountToken,
  }
}
